import { useRouter } from 'next/router';
import React from 'react';
import { NextUrqlPageContext } from 'next-urql';

import { VARIANT_QUERY } from 'gql/queries/product';
import { CATEGORY_BY_ID_QUERY } from 'gql/queries/category';
import {
  fetchBreadcrumbRedirectPromises,
  createProductBreadcrumbLinks,
} from 'utils/breadcrumbRedirects';
import sites from 'sites';
import { SUPPORTED_COUNTRY, isServer } from 'utils/constants';
import { parseRequestUrl } from 'utils/locale';
import { Page } from 'ui/components/Page';
import { useTranslate } from 'hooks/useTranslations';
import { PageHead } from 'ui/components/PageHead';
import { PdpProvider, usePdp } from 'hooks/usePdp';
import { useSiteConfig } from 'hooks/useSiteConfig';
import {
  AddToCartErrorMessage,
  ProductBreadcrumb,
  ProductPersonalise,
} from 'ui/pages/ProductDetailsPages';
import NotFoundPage from 'pages/404';
import { ProductStory } from 'ui/components/ProductStory';
import { PixleeProductWidget } from 'ui/components/PixleeProductWidget';
import { ProductCustomizationProvider } from 'hooks/useProductCustomization';
import { useMiniCart } from 'hooks/useMiniCart';
import { MiniCart } from 'ui/components/MiniCart';
import { useFeature } from 'hooks/useFeature';
import {
  useProductScoreQuery,
  PdpDocument,
  ProductQueryVariables,
  VariantQuery,
  VariantQueryVariables,
  PdpQuery,
} from '__generated__/graphql';
import { SanityContentSlot } from 'ui/components/SanityContentSlot';
import { avoidEmptyProperties } from 'utils/object';
import { InjectedScript } from 'ui/elements/InjectedScript';
import { useIsServer } from 'hooks/useIsServer';

const VARIANT_ID_MIN_LENGTH = 12; // variant IDs will be 12+ digits long
/** Checks if the length of the given `id` is gte `VARIANT_ID_MIN_LENGTH`. */
const isVariantId = (id: string) => id.length >= VARIANT_ID_MIN_LENGTH;

const Personalise = () => {
  const t = useTranslate();
  const router = useRouter();
  const { masterProduct, variation, content } = usePdp();
  const { currency, host } = useSiteConfig();
  const personalizationEnabled = useFeature('PERSONALIZATION_ENABLED');
  const reviewsEnabled = useFeature('RATINGS_AND_REVIEWS');
  const { isMiniCartVisible } = useMiniCart();
  const isServer = useIsServer();

  const [productScoreResult] = useProductScoreQuery({
    variables: {
      id: masterProduct?.id as string,
    },
    pause: !masterProduct?.id || !isServer || !reviewsEnabled,
  });
  const productScore = productScoreResult.data?.productScore;

  if (
    !masterProduct ||
    !variation ||
    !variation?.configureID?.enabled ||
    !personalizationEnabled
  ) {
    return <NotFoundPage message={t('productCouldNotBeFound')} />;
  }

  return (
    <>
      <ProductCustomizationProvider variation={variation}>
        <PageHead
          title={`${
            masterProduct?.name ||
            decodeURIComponent(router.query.model as string).replace(/-/g, ' ')
          } | PUMA`}
          description={masterProduct?.description ?? ''}
          noIndex={true}
          noIndexFollow={true}
        />
        <InjectedScript
          type="application/ld+json"
          script={JSON.stringify(
            avoidEmptyProperties({
              '@context': 'http://schema.org',
              '@type': 'Product',
              name: masterProduct?.name,
              sku: variation?.ean,
              color: variation?.name,
              description: masterProduct?.description,
              model: masterProduct?.id,
              brand: {
                '@type': 'Brand',
                name: masterProduct?.brand || 'PUMA',
              },
              logo: `https://${host}/assets/logo-puma-black.png`,
              image: variation?.images.map(x => x.href),
              offers: {
                '@type': 'Offer',
                price: variation?.salePrice || variation?.price,
                priceCurrency: currency.code,
                availability: 'http://schema.org/InStock',
                priceValidUntil: variation?.validUntil,
                url: !isServer ? window.location.href : router.asPath,
                seller: {
                  '@type': 'Corporation',
                  name: 'PUMA',
                },
              },
              aggregateRating:
                productScore && Number(productScore.amount) > 0
                  ? {
                      '@type': 'AggregateRating',
                      ratingValue: productScore.rating,
                      reviewCount: productScore.amount,
                      bestRating: 5,
                      worstRating: 1,
                    }
                  : undefined,
              review: (productScore?.reviews?.nodes || []).map(review => ({
                '@type': 'Review',
                author: {
                  '@type': 'Person',
                  name: review.nickname,
                },
                datePublished: review.date,
                name: review.title,
                reviewBody: review.text,
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: review.rating,
                  bestRating: 5,
                  worstRating: 1,
                },
              })),
            })
          )}
        />
        <Page>
          <div className="mobile:pb-4 tablet:pb-6 desktop:pb-8">
            <ProductBreadcrumb />
          </div>
          <section className="flex flex-col space-y-4 sm:space-y-8">
            <AddToCartErrorMessage />
            <ProductPersonalise />
            <SanityContentSlot
              items={content?.aboveProductStory}
              slotId="aboveProductStory"
              documentId={masterProduct?.id}
            />
            {variation?.productStory && (
              <ProductStory
                {...variation.productStory}
                additionalContentSections={
                  content?.ProductStoryAdditionalContentSlot
                }
                documentId={content?._id}
                slotId="ProductStoryAdditionalContentSlot"
              />
            )}
            {masterProduct?.id && (
              <PixleeProductWidget skuId={masterProduct.id} />
            )}
            <SanityContentSlot
              items={content?.belowProductStory}
              slotId="belowProductStory"
              documentId={content?._id}
            />
          </section>
          {isMiniCartVisible && <MiniCart />}
        </Page>
      </ProductCustomizationProvider>
    </>
  );
};

const Pdp = props => {
  return (
    <PdpProvider>
      <Personalise {...props} />
    </PdpProvider>
  );
};

if (isServer) {
  Pdp.getInitialProps = async (context: NextUrqlPageContext) => {
    const id = context.query.id as string;
    const req = context.req!;
    const { country } = parseRequestUrl(req);
    const siteConfig = sites[country.toUpperCase() as SUPPORTED_COUNTRY];
    const personalizationEnabled =
      siteConfig?.staticFeatures?.personalizationEnabled;

    if (isVariantId(id)) {
      const { data } = await context.urqlClient
        .query<VariantQuery, VariantQueryVariables>(VARIANT_QUERY, { id })
        .toPromise();

      if (!data?.variant || !personalizationEnabled) {
        if (context.res) context.res.statusCode = 404;
      }

      return {}; // Fall through to allow client-side 404 page to render
    }

    const pdpQuery = await context.urqlClient
      .query<PdpQuery, ProductQueryVariables>(PdpDocument, { id })
      .toPromise();

    const masterProduct = pdpQuery?.data?.product;
    const variations = masterProduct?.variations;

    // Pre-load breadcrumb queries
    if (masterProduct && masterProduct.primaryCategoryId) {
      const categoryByIdQuery = context.urqlClient.query<any, { id: string }>(
        CATEGORY_BY_ID_QUERY,
        { id: masterProduct.primaryCategoryId }
      );

      const categoryResult = await categoryByIdQuery.toPromise();
      const categoryById = categoryResult.data?.categoryById;

      if (categoryById) {
        const breadcrumbLinks = createProductBreadcrumbLinks({
          categoryById,
          productName: masterProduct.name,
          productLink: `pd/${context.query.model}/${context.query.id}`,
          isPersonalizing: true,
        });

        const redirectPromises = fetchBreadcrumbRedirectPromises(
          context.urqlClient,
          [...breadcrumbLinks]
        );

        // Execute all redirect promises
        await Promise.all(redirectPromises);
      }
    }

    const desiredSwatch =
      (context.query.swatch as string | undefined) ||
      masterProduct?.variations[0].colorValue;

    if (!masterProduct || variations?.length === 0) {
      if (context.res) context.res.statusCode = 404;
      return {}; // Fall through to allow client-side 404 page to render
    }

    const variation = desiredSwatch
      ? variations?.find(x => x.colorValue === desiredSwatch)
      : null;

    if (
      !variation ||
      !variation?.configureID?.enabled ||
      !personalizationEnabled
    ) {
      if (context.res) context.res.statusCode = 404;
      return {}; // Fall through to allow client-side 404 page to render
    }

    return { emptyProps: true };
  };
}

export default Pdp;
